import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ConcreteModelsService, CuringPoolssService, DevicesService, MeasurementsService, StagesService } from '@cosmos/angular/api';
import { ErrorService, MeasurementRefreshService, PanelService, StateService, RBACService, EntitlementService } from '@cosmos/angular/utils';
import { BreadcumbsService } from '../../navigation/breadcrumbs/breadcrumbs.service';
import { MonitoringTreeService } from '../../monitoring/monitoring-tree.service';

import { Concrete, CuringPool, Device, isMeasurementStarted, Measurement, Project, User } from '@cosmos/shared';


import { EditMeasurementForm, MeasurementChangeDeviceForm, MeasurementRescheduleForm } from '../../dynamic-form/forms/measurement.form';
import { FileUploadService } from '../../../legacy/file-upload/file-upload.service';
import { DynamicFormDialog } from '../../dynamic-form/dialog-form/dialog-form.component';
import { ReportsManagerComponent } from '../../components/reports-manager/reports-manager.component';
import { MeasurementActionsMenuComponent, MeasurementAction } from '../../components/measurement-actions-menu/measurement-actions-menu.component';
import { MeasurementInfoCardComponent } from '../../components/measurement-info-card/measurement-info-card.component';
import { MeasurementChangeStageDialogComponent } from '../../components/measurement-change-stage-dialog/measurement-change-stage-dialog.component';
import { MeasurementChangeConcreteDialogComponent } from '../../components/measurement-change-concrete-dialog/measurement-change-concrete-dialog.component';

import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogModule, DialogService } from 'primeng/dynamicdialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';


import { StrengthInsightsCardComponent } from '../../cards/insights/stength-insights-card/stength-insights-card.component';
import { DynamicLineChartComponent } from '../../data/charts/dynamic-line-chart/dynamic-line-chart.component';
import { PredictiveChartComponent } from '../../data/charts/predictive-chart/predictive-chart.component';
import { DatapointClickEvent } from '../../data/charts/multi-line-chart/multi-line-chart.component';
import { MaturityDatapointAdapter } from '../datapoint/adapters/maturity-datapoint.adapter';
import { DatapointDetailsComponent } from '../datapoint/datapoint-details/datapoint-details.component';


@Component({
  selector: 'cosmos-maturity-measurement',
  templateUrl: './maturity-measurement.component.html',
  imports: [TranslatePipe, StrengthInsightsCardComponent, DynamicLineChartComponent, PredictiveChartComponent,
    CardModule, DividerModule, ButtonModule, DynamicDialogModule, ConfirmDialogModule, MeasurementActionsMenuComponent,
    MeasurementInfoCardComponent,
  ],
  providers: [DialogService, ConfirmationService, TranslateService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaturityMeasurementComponent {
  private readonly errorService = inject(ErrorService);
  readonly rbac = inject(RBACService);
  readonly dialogService = inject(DialogService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly measurementsService = inject(MeasurementsService);
  private readonly concretesService = inject(ConcreteModelsService);
  private readonly devicesService = inject(DevicesService);
  private readonly stagesService = inject(StagesService);
  private readonly filesService = inject(FileUploadService);
  private readonly stateService = inject(StateService);
  private readonly refreshService = inject(MeasurementRefreshService);
  private readonly breadcumbsService = inject(BreadcumbsService);
  private readonly treeService = inject(MonitoringTreeService);
  readonly panelService = inject(PanelService);
  private readonly curingPoolService = inject(CuringPoolssService);
  readonly entitlementService = inject(EntitlementService);

  readonly measurementId = input<string>();

  // Signals — mutations trigger OnPush re-render
  readonly measurement = signal<Measurement | null>(null);
  readonly concrete = signal<Concrete | null>(null);
  readonly device = signal<Device | null>(null);
  readonly project = signal<Project | null>(null);
  readonly predictions = signal<any>(null);
  readonly curingPool = signal<CuringPool | null>(null);
  readonly chartData = signal<any>(null);
  readonly maturityChartFields = signal<string[]>([]);
  readonly maturityChartThreshold = signal<Record<string, any>>({});
  readonly strengthChartThreshold = signal<Record<string, any>>({});

  // IN DEVELOPMENT - Relative maturity
  relativeMaturity: number[];

  user: User;
  generatingReport = false;

  constructor() {
    this.stateService.currentUser$
      .pipe(takeUntilDestroyed())
      .subscribe(x => { this.user = x; });

    effect(() => {
      if (this.measurementId()) {
        this.initData();
      }
    });
  }

  initData(): void {
    // Reset accumulated chart config before loading new measurement data
    this.maturityChartFields.set([]);
    this.maturityChartThreshold.set({});
    this.strengthChartThreshold.set({});

    this.measurementsService.findById(this.measurementId(), '/?populate_project&populate_device=name,deviceProfile&populate_concrete=name').subscribe({
      next: (response) => {
        this.project.set(response.body.project);
        this.measurement.set(response.body);
      },
      error: (e) => this.errorService.handleError(e),
      complete: () => {
        const m = this.measurement();
        const p = this.project();
        this.getData();

        if (m.curingPoolData.length > 0 && m.curingPoolFinished) {
          this.chartData.set({
            measurement: { time: m.time, temperature: m.temperature },
            pool: { time: m.curingPoolData[0].time, temperature: m.curingPoolData[0].temperature }
          });
        } else {
          this.chartData.set(null);
        }

        if (m.specifications != undefined && m.specifications.length) {
          this.strengthChartThreshold.set({ strength: m.specifications[m.specifications.length - 1] });
        }

        // MATURITY CHART SETUP
        const fields: string[] = [];
        const thresholds: Record<string, any> = {};

        if (p.visualizationConfig?.maturity?.app) {
          fields.push('maturity');
          if (p.visualizationConfig?.maturityThreshold?.app) {
            thresholds['maturity'] = m.specifications_maturity.length
              ? m.specifications_maturity[m.specifications_maturity.length - 1]
              : null;
          }
        }

        if (p.visualizationConfig?.relativeMaturity?.app) {
          fields.push('relativeMaturity');
          if (p.visualizationConfig?.relativeMaturityThreshold?.app) {
            thresholds['relativeMaturity'] = m.specifications_maturity_relative.length
              ? m.specifications_maturity_relative[m.specifications_maturity_relative.length - 1]
              : null;
          }
        }

        this.maturityChartFields.set(fields);
        this.maturityChartThreshold.set(thresholds);

        if (this.entitlementService.hasFeature('monitoring', 'monitoring.charts.strength_prediction')) {
          this.measurementsService.getPredictions(this.measurementId(), p._id).subscribe({
            next: (response) => { this.predictions.set(response.body); },
            error: (e) => this.errorService.handleError(e),
          });
        }
      },
    });
  }

  getData() {
    const m = this.measurement();
    this.concrete.set(m.concrete as Concrete || { name: '-' });
    this.device.set(m.device as Device || { name: '-' });

    this.curingPoolService.find('?measurement=' + m._id).subscribe({
      next: (event: any) => {
        if (event.body.length) this.curingPool.set(event.body[0]);
        else this.curingPool.set(null);
      },
      error: (e) => this.errorService.handleError(e),
      complete: () => {
        const pool = this.curingPool();
        if (pool?.time.length > 0) {
          this.chartData.set({
            measurement: { time: m.time, temperature: m.temperature },
            pool: { time: pool.time, temperature: pool.temp }
          });
        } else {
          this.chartData.set(null);
        }
      },
    });

    this.calculateRelativeMaturity();
  }

  editMeasurement() {
    this.openDialog();
  }

  finishMeasurement($event: Event) {
    this.confirmationService.confirm({
      target: $event.currentTarget as EventTarget,
      message: this.translateService.instant('measurement.finish-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: this.translateService.instant('button.finish')
      },
      accept: () => {
        this.measurementsService.finish(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.finishSuccess',
            onSuccess: () => {
              this.initData();
              this.refreshService.trigger();
            },
          }),
        );
      },
      reject: () => null
    });
  }

  changeDevice(measurement: Measurement) {
    this.devicesService.find(`/?project=${this.measurement().project._id}&available=true&deviceType=DataAcquisition&deviceProfile=${this.device().deviceProfile}`).subscribe({
      next: (response) => {
        const devices = response.body;
        if (devices.length > 0) {
          const measurementChangeDeviceForm = new MeasurementChangeDeviceForm(this.translateService, devices, this.user, {});
          const dialogRef = this.dialogService.open(DynamicFormDialog, {
            data: { form: measurementChangeDeviceForm },
            header: this.translateService.instant('measurement.change_device'),
            modal: true, closable: true, width: '50vw',
            breakpoints: { '960px': '75vw', '640px': '90vw' },
          });
          dialogRef.onClose.subscribe(result => {
            if (result !== undefined) {
              this.measurementsService.changeDevice(this.measurement()._id, result).subscribe(
                this.errorService.handleMutation({
                  successMessageKey: 'measurement.messages.updateSuccess',
                  onSuccess: () => this.initData(),
                }),
              );
            }
          });
        } else {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
      error: (e) => {
        if (e.error.message == 'Devices not Found') {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
    });
  }

  moveStage(measurement: Measurement) {
    this.fetchProjectStageTree(this.project()._id).subscribe({
      next: (stageTree) => {
        const dialogRef = this.dialogService.open(MeasurementChangeStageDialogComponent, {
          data: { measurement, stageTree },
          header: this.translateService.instant('measurement.move_stage.title'),
          modal: true, closable: true, width: '30vw',
          breakpoints: { '960px': '75vw', '640px': '90vw' },
        });
        dialogRef.onClose.subscribe((result: { stage: string } | undefined) => {
          if (result !== undefined) {
            this.measurementsService.changeStage(measurement._id, result).subscribe(
              this.errorService.handleMutation({
                successMessageKey: 'measurement.messages.updateSuccess',
                onSuccess: () => {
                  // The measurement's URL no longer encodes its stage
                  // (flat /monitoring/measurement/:id route), so a stage-only
                  // move doesn't change the URL at all — no navigate needed.
                  // MeasurementPage's fetch is keyed off the (unchanged)
                  // :measurement param, so it wouldn't otherwise re-run.
                  this.refreshService.trigger();
                  // The measurement moved to a different stage — the tree rail
                  // must re-parent its leaf.
                  this.treeService.refresh();
                  // Breadcrumbs are only re-resolved on real navigation; a
                  // stage-only move doesn't change the URL, so nothing else
                  // would refresh the (now stale) breadcrumb trail.
                  this.breadcumbsService.refresh();
                  // Same reason this component's own `measurement` signal
                  // (built from `measurementId()`, not from the route's stage
                  // param) needs an explicit refetch — otherwise re-opening
                  // this dialog would show the pre-move stage again.
                  this.initData();
                },
              }),
            );
          }
        });
      },
      error: (e) => this.errorService.handleError(e),
    });
  }

  // Fetches every root stage in the project, then each root's full
  // descendant tree — the shape the change-stage dialog's tree picker needs.
  private fetchProjectStageTree(projectId: string) {
    return this.stagesService.find(`?project=${projectId}&rootOnly=true`).pipe(
      switchMap((rootsRes) => {
        const roots = (rootsRes.body as any[]) ?? [];
        if (roots.length === 0) return of([]);
        return this.stagesService.getForest(roots.map((r) => r._id)).pipe(map((res) => res.body ?? []));
      }),
    );
  }

  changeConcrete(measurement: Measurement) {
    this.concretesService.find(`?project=${this.project()._id}&isActive=true`).subscribe({
      next: (response) => {
        const concretes = response.body;
        const dialogRef = this.dialogService.open(MeasurementChangeConcreteDialogComponent, {
          data: { measurement, concretes },
          header: this.translateService.instant('measurement.change_concrete.title'),
          modal: true, closable: true, width: '40vw',
          breakpoints: { '960px': '75vw', '640px': '90vw' },
        });
        dialogRef.onClose.subscribe((result: { concrete: string; specifications: number[]; specifications_maturity: number[]; specifications_maturity_relative: number[] } | undefined) => {
          if (result !== undefined) {
            this.measurementsService.changeConcrete(measurement._id, result).subscribe(
              this.errorService.handleMutation({
                successMessageKey: 'measurement.messages.updateSuccess',
                onSuccess: () => this.initData(),
              }),
            );
          }
        });
      },
      error: (e) => this.errorService.handleError(e),
    });
  }

  openDialog() {
    const m = this.measurement();
    const editMeasurementForm = new EditMeasurementForm(this.translateService, m, this.user, {
      visualizationConfig: this.project().visualizationConfig, measurementType: m.measurementType
    });
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: editMeasurementForm, dialogTitle: this.translateService.instant('dialog.title.edit.measurement') },
      header: this.translateService.instant('dialog.title.edit.measurement'),
      modal: true, closable: true, width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe(result => {
      if (result !== undefined) {
        this.measurementsService.update([m._id, result]).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.updateSuccess',
            onSuccess: () => this.initData(),
          }),
        );
      }
    });
  }

  downloadCSV() {
    this.measurementsService.downloadCSV('_id=' + this.measurement()._id).subscribe({
      next: (response) => { this.filesService.downloadCSVFromBlob(response); },
      error: () => {
        this.errorService.notify('error', this.translateService.instant('measurement.download.error'));
      }
    });
  }

  openRescheduleDialog(measurement: Measurement) {
    if (measurement.timeAll.length) {
      const m = this.measurement();
      const startTimestamp = m.timeAll.findIndex(time => time == measurement.time[0]);
      const endTimestamp = m.timeAll.findIndex(time => time == measurement.time[measurement.time.length - 1]);
      const rescheduleForm = new MeasurementRescheduleForm(this.translateService, measurement.timeAll, startTimestamp, endTimestamp, this.user, {});

      const dialogRef = this.dialogService.open(DynamicFormDialog, {
        data: { form: rescheduleForm },
        header: this.translateService.instant('dialog.title.reschedule'),
        modal: true, closable: true, width: '50vw',
        breakpoints: { '960px': '75vw', '640px': '90vw' },
      });

      dialogRef.onClose.subscribe(result => {
        if (result !== undefined) {
          this.measurementsService.reschedule(this.measurementId(), result.timestampStart, result.timestampEnd).subscribe(
            this.errorService.handleMutation({
              successMessageKey: 'measurement.reschedule.success',
              onSuccess: () => this.initData(),
            }),
          );
        }
      });
    } else {
      this.errorService.notify('warn', this.translateService.instant('measurement.reschedule.no-data'));
    }
  }

  calculateRelativeMaturity() {
    const m = this.measurement();
    const relativeMaturityCalculation = [0];
    if (m.time.length == 1) {
      return;
    }

    const timeMillis = m.time.map(x => new Date(x).getTime());

    timeMillis.slice(1).forEach((timestamp, i) => {
      const time_step = (timestamp - timeMillis[i]) / (86400000);
      const calculated_maturity = (relativeMaturityCalculation[i] + time_step * Math.max((m.temperature[i + 1] - m.temperature[0]), 0));
      relativeMaturityCalculation.push(Number(Math.max(Number(calculated_maturity), 0)));
    });

    // Mutate the measurement signal value to add relativeMaturity
    this.measurement.update(current => ({ ...current, relativeMaturity: relativeMaturityCalculation }));
    this.relativeMaturity = relativeMaturityCalculation;
  }

  openManageReports() {
    this.panelService.initialize(ReportsManagerComponent, { measurement: this.measurement() });
  }

  unstartMeasurement(event?: Event) {
    this.confirmationService.confirm({
      ...(event ? { target: event.currentTarget as EventTarget } : {}),
      message: this.translateService.instant('measurement.unstart-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: this.translateService.instant('measurement.unstart')
      },
      accept: () => {
        this.measurementsService.unstart(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.unstartSuccess',
            // Refreshes the parent page's `measurement` signal, since its
            // viewMode depends on `started` and won't otherwise notice this
            // component flipped it — see MeasurementRefreshService.
            onSuccess: () => this.refreshService.trigger(),
          }),
        );
      },
      reject: () => null
    });
  }

  onDatapointClick(event: DatapointClickEvent): void {
    const m = this.measurement();
    if (!m?.time?.length) return;

    const series = [
      { key: 'temperature', label: this.translateService.instant('chart.legend.temperature'), unit: '°C', values: (m as any).temperature ?? [] },
      { key: 'maturity', label: this.translateService.instant('chart.legend.maturity'), unit: '°C·h', values: (m as any).maturity ?? [] },
      { key: 'strength', label: this.translateService.instant('chart.legend.strength'), unit: 'MPa', values: (m as any).strength ?? [] },
    ].filter((s) => s.values.length === m.time.length);

    const adapter = new MaturityDatapointAdapter({
      measurementId: this.measurementId(),
      measurementName: m.name,
      time: m.time,
      series,
      startIndex: event.index,
      highlightKey: event.variable,
    });
    this.panelService.initialize(DatapointDetailsComponent, { adapter });
  }

  readonly tier2Actions = computed<MeasurementAction[]>(() => {
    const m = this.measurement();
    const actions: MeasurementAction[] = [
      { labelKey: 'measurement.download-csv', icon: 'fa-solid fa-download', action: () => this.downloadCSV() },
      { labelKey: 'measurement.manage-reports', icon: 'fa-solid fa-file-lines', action: () => this.openManageReports() },
      { labelKey: 'measurement.move_stage.action', icon: 'fa-solid fa-layer-group', action: () => this.moveStage(m) },
    ];
    if (this.rbac.isSuperAdmin()) {
      actions.push({ labelKey: 'measurement.change_concrete.action', icon: 'fa-solid fa-flask', action: () => this.changeConcrete(m) });
    }
    if (m?.active) {
      actions.push({ labelKey: 'measurement.reschedule.button', icon: 'fa-solid fa-clock-rotate-left', action: () => this.openRescheduleDialog(m) });
      if (isMeasurementStarted(m)) {
        actions.push({ labelKey: 'measurement.unstart', icon: 'fa-solid fa-rotate-left', action: () => this.unstartMeasurement() });
      }
    }
    return actions;
  });
}
